import {DocumentNode, OperationVariables} from '@apollo/client';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {AccessibleGraphQLObject, accessibleObjs} from 'graphql-client/InaccessibleInfo';

import {InaccessibleInfoGraphQL} from '../../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {CardSectionRowProps, renderQueryLoadingOrError} from '../CardSection';

interface UseMyWorkRowsOptions<
  TData,
  TVariables extends OperationVariables,
  TItem extends AccessibleGraphQLObject & {modifiedAt: string},
> {
  query: DocumentNode;
  variables: TVariables;
  /** True while a parent summary query has not resolved yet (row IDs are still null). */
  skip: boolean;
  getItems: (data: TData) => Array<TItem | InaccessibleInfoGraphQL>;
  toRowProps: (item: TItem) => CardSectionRowProps;
  errorMessage: string;
  testComponent: string;
}

type UseMyWorkRowsResult =
  | {type: QueryResultType.SUCCESS; rows: Array<CardSectionRowProps>}
  | {type: Exclude<QueryResultType, QueryResultType.SUCCESS>; element: JSX.Element};

export function useMyWorkRows<
  TData,
  TVariables extends OperationVariables,
  TItem extends AccessibleGraphQLObject & {modifiedAt: string},
>({
  query,
  variables,
  skip,
  getItems,
  toRowProps,
  errorMessage,
  testComponent,
}: UseMyWorkRowsOptions<TData, TVariables, TItem>): UseMyWorkRowsResult {
  const queryResult = useSkippableBenchlingQuery<TData, TVariables>(query, {
    variables,
    skip,
  });

  if (queryResult.type === QueryResultType.SUCCESS) {
    const accessibleItems = accessibleObjs<TItem>(getItems(queryResult.data));
    const rows = accessibleItems
      .sort((a, b) => b.modifiedAt.localeCompare(a.modifiedAt))
      .map((item) => toRowProps(item));

    return {type: QueryResultType.SUCCESS, rows};
  }

  // When skip is set, Apollo reports SKIPPED; reuse the PENDING loading UI until the summary pre-query
  // yields IDs. Callers still see type SKIPPED (not PENDING) — they only branch on SUCCESS vs non-SUCCESS.
  const element = renderQueryLoadingOrError(
    {type: skip ? QueryResultType.PENDING : queryResult.type},
    errorMessage,
    testComponent
  )!;

  return {type: queryResult.type, element};
}
